import { useMemo, useState } from "react";
import { Plus, Trash2 } from "lucide-react";

import { cn } from "@/lib/cn";
import { lintExpression } from "@/data/alarmRules/expressionLint";
import { VALID_AGG_OPERATORS, type AggOperator } from "@/data/alarmRules/engineConstants";
import type { ExpressionDatapointSpec } from "@/data/alarmRules/wireFormat";
import { NumberField } from "@/components/ui/NumberField";

import { DatapointPicker } from "./DatapointPicker";

interface Props {
  expression: string;
  onExpressionChange: (next: string) => void;
  datapoints: Record<string, ExpressionDatapointSpec>;
  onDatapointsChange: (next: Record<string, ExpressionDatapointSpec>) => void;
  deviceId: string | null;
}

function isAggregated(
  spec: ExpressionDatapointSpec,
): spec is { topic: string; agg: AggOperator; window_s: number } {
  return typeof spec !== "string";
}

export function ExpressionEditor({
  expression,
  onExpressionChange,
  datapoints,
  onDatapointsChange,
  deviceId,
}: Props) {
  const aliases = useMemo(() => Object.keys(datapoints), [datapoints]);
  const lint = useMemo(
    () => lintExpression(expression, aliases),
    [expression, aliases],
  );
  const [newAlias, setNewAlias] = useState("");

  const addAlias = (alias: string) => {
    const trimmed = alias.trim();
    if (!trimmed || !/^[A-Za-z_][A-Za-z0-9_]*$/.test(trimmed)) return;
    if (datapoints[trimmed]) return;
    onDatapointsChange({ ...datapoints, [trimmed]: "" });
    setNewAlias("");
  };

  const removeAlias = (alias: string) => {
    const next = { ...datapoints };
    delete next[alias];
    onDatapointsChange(next);
  };

  const updateAlias = (alias: string, spec: ExpressionDatapointSpec) => {
    onDatapointsChange({ ...datapoints, [alias]: spec });
  };

  return (
    <div className="space-y-3">
      <div>
        <label className="text-[11px] uppercase tracking-wider text-text-faint">
          Expression
        </label>
        <textarea
          value={expression}
          onChange={(e) => onExpressionChange(e.target.value)}
          rows={3}
          spellCheck={false}
          className={cn(
            "form-input w-full font-mono text-[12px] leading-snug",
            !lint.ok && "border-critical/60",
          )}
          placeholder="(soc < 20) and (P_setpoint > 5000)"
        />
        <div className="mt-1 text-[10px] text-text-faint">
          Allowed: <code>+ - * / // % **</code>, comparisons, <code>and / or / not</code>,
          and <code>min / max / abs / len</code> (nested up to depth 2).
        </div>
        {lint.issues.length > 0 && (
          <ul className="mt-2 space-y-1">
            {lint.issues.map((issue, i) => (
              <li
                key={i}
                className={cn(
                  "text-[11px] px-2 py-1 rounded border",
                  issue.severity === "error"
                    ? "border-critical/40 bg-critical/10 text-critical"
                    : "border-warn/40 bg-warn/10 text-warn",
                )}
              >
                <span className="font-semibold uppercase tracking-wider mr-2">
                  {issue.severity}
                </span>
                {issue.message}
                {issue.position !== undefined && (
                  <span className="ml-1 text-text-faint">
                    @ position {issue.position}
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="border border-edge rounded">
        <div className="px-3 py-2 border-b border-edge bg-surface-2/40 flex items-center gap-2">
          <span className="text-[11px] uppercase tracking-wider text-text-faint">
            Aliases ({aliases.length})
          </span>
          <span className="text-[11px] text-text-faint">
            Identifier → datapoint topic (optional rolling-window agg).
          </span>
        </div>
        <div className="divide-y divide-edge/40">
          {aliases.length === 0 && (
            <div className="p-3 text-[12px] text-text-faint">
              Add at least one alias before saving.
            </div>
          )}
          {aliases.map((alias) => {
            const spec = datapoints[alias];
            const aggregated = isAggregated(spec);
            return (
              <div key={alias} className="p-3 space-y-2">
                <div className="flex items-center gap-2">
                  <code className="text-[12px] bg-surface-2/60 px-1.5 py-0.5 rounded">
                    {alias}
                  </code>
                  <label className="ml-auto flex items-center gap-1 text-[11px] text-text-faint">
                    <input
                      type="checkbox"
                      checked={aggregated}
                      onChange={(e) => {
                        if (e.target.checked) {
                          updateAlias(alias, {
                            topic: typeof spec === "string" ? spec : "",
                            agg: "avg",
                            window_s: 60,
                          });
                        } else {
                          updateAlias(
                            alias,
                            typeof spec === "string" ? spec : spec.topic,
                          );
                        }
                      }}
                    />
                    Rolling window
                  </label>
                  <button
                    type="button"
                    onClick={() => removeAlias(alias)}
                    className="text-text-faint hover:text-critical"
                    aria-label={`Remove alias ${alias}`}
                  >
                    <Trash2 className="size-3.5" />
                  </button>
                </div>
                <DatapointPicker
                  deviceId={deviceId}
                  value={typeof spec === "string" ? spec : spec.topic}
                  onChange={(topic) => {
                    if (typeof spec === "string") {
                      updateAlias(alias, topic);
                    } else {
                      updateAlias(alias, { ...spec, topic });
                    }
                  }}
                />
                {aggregated && (
                  <div className="grid grid-cols-2 gap-2">
                    <label className="text-[11px] text-text-faint flex flex-col gap-1">
                      Aggregation
                      <select
                        value={spec.agg}
                        onChange={(e) =>
                          updateAlias(alias, { ...spec, agg: e.target.value as AggOperator })
                        }
                        className="form-input"
                      >
                        {VALID_AGG_OPERATORS.map((a) => (
                          <option key={a} value={a}>
                            {a}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label className="text-[11px] text-text-faint flex flex-col gap-1">
                      Window (s)
                      <NumberField
                        value={spec.window_s}
                        onChange={(v) =>
                          updateAlias(alias, { ...spec, window_s: Math.max(1, v) })
                        }
                        min={1}
                        className="form-input"
                      />
                    </label>
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <div className="p-3 border-t border-edge flex items-center gap-2">
          <input
            type="text"
            value={newAlias}
            onChange={(e) => setNewAlias(e.target.value)}
            placeholder="new_alias"
            className="form-input flex-1 font-mono"
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                addAlias(newAlias);
              }
            }}
          />
          <button
            type="button"
            onClick={() => addAlias(newAlias)}
            disabled={!newAlias || !!datapoints[newAlias.trim()]}
            className="text-[11px] uppercase tracking-wide px-3 py-1.5 rounded border border-edge hover:bg-surface-2/60 flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Plus className="size-3.5" /> Add alias
          </button>
        </div>
      </div>
    </div>
  );
}
